import { createAction, props } from '@ngrx/store';

export interface SearchParams {
  filters: any;
  page: number;
  pageSize: number;
}

export interface SearchResponse {
  data: any[];
  message: string;
}

export interface LocationSearchParams {
  country?: string;
  region?: string;
  city?: string;
}

export interface LocationSearchResponse {
  data: any[];
  message: string;
}

export interface SearchPeopleParams {
  companyName: string;
  selectedCompany: any;
  page?: number;
  perPage?: number;
  person_titles?: string[];
  person_seniorities?: string[];
  person_locations?: string;
  organization_locations?: string;
  website_domain?: string;
}

// Define the possible payload types
type SearchPeoplePayload =
  | {
  companyName: string;
  selectedCompany: any;
  page?: number;
  perPage?: number;
}
  | { params: SearchPeopleParams };

// Create a factory function for the action creator
function createSearchPeopleAction() {
  return createAction(
    '[Advanced Search] Search People',
    (payload: SearchPeoplePayload) => {
      // Normalize the payload to always have 'params' format
      if ('companyName' in payload) {
        return {
          params: {
            companyName: payload.companyName,
            selectedCompany: payload.selectedCompany,
            page: payload.page,
            perPage: payload.perPage
          }
        };
      }
      return payload;
    }
  );
}

export const searchCompanies = createAction(
  '[Advanced Search] Search Companies',
  props<{ filters: any; page: number; pageSize: number; nextPageToken?: string; }>()
);

export const searchCompaniesSuccess = createAction(
  '[Advanced Search] Search Companies Success',
  props<{ response: { data: any; } }>()
);

export const searchCompaniesFailure = createAction(
  '[Advanced Search] Search Companies Failure',
  props<{ error: any }>()
);

export const searchPeople = createSearchPeopleAction();

export const searchPeopleSuccess = createAction(
  '[Advanced Search] Search People Success',
  props<{ response: { data: any; message: string } }>()
);

export const searchPeopleFailure = createAction(
  '[Advanced Search] Search People Failure',
  props<{ error: any }>()
);

export const clearPeople = createAction(
  '[Advanced Search] Clear People'
);

export const clearSearchResults = createAction(
  '[Advanced Search] Clear Search Results'
);

export const saveSearchFilters = createAction(
  '[Advanced Search] Save Search Filters',
  props<{ filters: any, filterTypes: any[] }>()
);

export const loadSavedFilters = createAction(
  '[Advanced Search] Load Saved Filters'
);

export const setSavedFilters = createAction(
  '[Advanced Search] Set Saved Filters',
  props<{ filters: any, filterTypes: any[] }>()
);

export const clearSavedFilters = createAction(
  '[Advanced Search] Clear Saved Filters'
);

// Location Actions - Store-based approach
export const loadCountries = createAction(
  '[AdvancedSearch] Load Countries'
);

export const loadCountriesSuccess = createAction(
  '[AdvancedSearch] Load Countries Success',
  props<{ countries: any[] }>()
);

export const loadCountriesFailure = createAction(
  '[AdvancedSearch] Load Countries Failure',
  props<{ error: any }>()
);

export const loadRegions = createAction(
  '[AdvancedSearch] Load Regions',
  props<{
    countryId: string | number;
    countryName: string;
    veridionCountryId: string | number;
  }>()
);

export const loadRegionsSuccess = createAction(
  '[AdvancedSearch] Load Regions Success',
  props<{
    countryId: string | number;
    regions: any[]
  }>()
);

export const loadRegionsFailure = createAction(
  '[AdvancedSearch] Load Regions Failure',
  props<{ error: any }>()
);

export const loadCities = createAction(
  '[AdvancedSearch] Load Cities',
  props<{
    regionId: string | number;
    countryName: string;
    regionName: string;
    veridionCountryId: string | number;
    veridionRegionId: string | number;
  }>()
);

export const loadCitiesSuccess = createAction(
  '[AdvancedSearch] Load Cities Success',
  props<{
    regionId: string | number;
    cities: any[]
  }>()
);

export const loadCitiesFailure = createAction(
  '[AdvancedSearch] Load Cities Failure',
  props<{ error: any }>()
);

// Company Industries
export const searchCompanyIndustries = createAction(
  '[Advanced Search] Search Company Industries',
  props<{ searchTerm: string }>()
);

export const searchCompanyIndustriesSuccess = createAction(
  '[Advanced Search] Search Company Industries Success',
  props<{ response: any }>()
);

export const searchCompanyIndustriesFailure = createAction(
  '[Advanced Search] Search Company Industries Failure',
  props<{ error: any }>()
);

// Legacy actions for backward compatibility (deprecated)
export const searchLocations = createAction(
  '[AdvancedSearch] Search Locations',
  props<{ country?: string; region?: string; city?: string }>()
);

export const searchLocationsSuccess = createAction(
  '[AdvancedSearch] Search Locations Success',
  props<{ response: any }>()
);

export const searchLocationsFailure = createAction(
  '[AdvancedSearch] Search Locations Failure',
  props<{ error: any }>()
);

export const searchCountries = createAction(
  '[AdvancedSearch] Search Countries',
  props<{ searchTerm?: string }>()
);

export const searchCountriesSuccess = createAction(
  '[AdvancedSearch] Search Countries Success',
  props<{ response: any }>()
);

export const searchCountriesFailure = createAction(
  '[AdvancedSearch] Search Countries Failure',
  props<{ error: any }>()
);

export const searchRegions = createAction(
  '[AdvancedSearch] Search Regions',
  props<{
    countryName: string;
    veridionCountryId: string | number;
    searchTerm?: string
  }>()
);

export const searchRegionsSuccess = createAction(
  '[AdvancedSearch] Search Regions Success',
  props<{ response: any }>()
);

export const searchRegionsFailure = createAction(
  '[AdvancedSearch] Search Regions Failure',
  props<{ error: any }>()
);

export const searchCities = createAction(
  '[AdvancedSearch] Search Cities',
  props<{
    countryName: string;
    regionName: string;
    veridionCountryId: string | number;
    veridionRegionId: string | number;
    searchTerm?: string
  }>()
);

export const searchCitiesSuccess = createAction(
  '[AdvancedSearch] Search Cities Success',
  props<{ response: any }>()
);

export const searchCitiesFailure = createAction(
  '[AdvancedSearch] Search Cities Failure',
  props<{ error: any }>()
);

export const setNewSearchFlag = createAction(
  '[Advanced Search] Set New Search Flag',
  props<{ isNewSearch: boolean }>()
);

export const startSearch = createAction(
  '[Advanced Search] Start Search'
);
